import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type { ReactNode } from 'react'
import type { Action } from '@/shared/constants/actions'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { usePermissions } from '@/shared/hooks/use-permissions'

interface PermissionGateProps {
  action: Action
  children: ReactNode
  title?: string
  description?: string
}

export function PermissionGate({
  action,
  children,
  title,
  description,
}: PermissionGateProps) {
  const { can, isLoading } = usePermissions()
  const { t } = useTranslation('common')

  // Permissions are still being resolved (profile after a hard reload, or the role map):
  // render a quiet placeholder instead of flashing an access denial that is not yet true.
  if (isLoading && !can(action)) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    )
  }

  if (!can(action)) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{title ?? t('accessDenied')}</AlertTitle>
        <AlertDescription>
          {description ?? t('accessDeniedMessage')}
        </AlertDescription>
      </Alert>
    )
  }

  return <>{children}</>
}
